import React from 'react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import i18n from '@sprinklr/display-builder/i18n';

// components
import { Flexbox, IconButton } from 'components/spaceKit';
import ActionInput from 'components/_UI/Forms/Actions/ActionInput';
import ActionSelect from 'components/_UI/Forms/Actions/ActionSelect';
import { DimensionValuesSelection } from '../../dimensionValuesSelection/DimensionValuesSelection';
import { FieldSelection } from '../../fieldSelection/FieldSelection';
import { TimeDurationTunable } from '../../timeDurationTunable';
import { StyledMetricCondition, StyledDimensionCondition } from '../styledComponents';

// utils
import { getMetricDesignOptionsKey } from '../../metricDesignTunables/utils/getMetricDesignOptionsKey';
import { getIsDuration } from '../utils/getIsDuration';

// constants
import { METRIC_COMPARISON_OPTIONS, DIMENSION_COMPARISON_OPTIONS } from '../constants';

// types
import { ConditionProps } from './types';

function Condition({ condition, tag, index, widget }: ConditionProps) {
    const handleUpdateTimeDuration = (duration: number) => {
        runInAction(() => {
            condition.value = duration;
        });
    };
    const removeTag = () => {
        runInAction(() => tag.conditions.splice(index, 1));
    };

    const selectedMetric = widget.analyticsRequests?.[0]?.projections?.find(
        metric =>
            getMetricDesignOptionsKey(metric.measurementName, metric.aggregateFunction) ===
            condition.name
    );

    const isDuration =
        condition.type === 'METRIC' && selectedMetric && getIsDuration(selectedMetric);

    return (
        <Flexbox middle vertical gap='ml'>
            {condition.type === 'METRIC' ? (
                <StyledMetricCondition data-id='color-tag-condition'>
                    <FieldSelection widget={widget} colorTag={condition} />
                    <ActionSelect
                        option={condition}
                        value='comparison'
                        options={METRIC_COMPARISON_OPTIONS}
                    />
                    {isDuration ? (
                        <Flexbox>
                            <TimeDurationTunable
                                tag={condition}
                                onFormSubmit={handleUpdateTimeDuration}
                            />
                        </Flexbox>
                    ) : (
                        <ActionInput
                            numeric
                            type='number'
                            placeholder={i18n.t('Value')}
                            option={condition}
                            value='value'
                        />
                    )}
                    <IconButton
                        icon='icon-minus'
                        onClick={removeTag}
                        data-testid='icon-button-remove-color-tag-condition'
                    />
                </StyledMetricCondition>
            ) : (
                <StyledDimensionCondition data-id='color-tag-condition'>
                    <FieldSelection widget={widget} colorTag={condition} />
                    <ActionSelect
                        option={condition}
                        value='comparison'
                        options={DIMENSION_COMPARISON_OPTIONS}
                    />
                    <IconButton
                        style={{
                            gridRow: 'span 2',
                            alignSelf: 'center',
                        }}
                        icon='icon-minus'
                        onClick={removeTag}
                        data-testid='icon-button-remove-color-tag-condition'
                    />
                    <Flexbox style={{ gridColumn: 'span 2' }}>
                        <DimensionValuesSelection widget={widget} condition={condition} />
                    </Flexbox>
                </StyledDimensionCondition>
            )}
        </Flexbox>
    );
}

const ConditionHOC = observer(Condition);
export { ConditionHOC as Condition };
